import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  getActiveOperator,
  getOperatorType,
} from "../../../services/panel/panelServices";
import CategoryIcon from "../../component/CategoryIcon";

// Card colours rotate through the design's palette
const CAT_COLORS = [
  { bg: "linear-gradient(150deg,#FFD98A,#F5A524)", stroke: "#4A2E00" },
  { bg: "linear-gradient(150deg,#5CF0D4,#008C74)", stroke: "#04342C" },
  { bg: "linear-gradient(150deg,#C9B8FF,#7A5CE0)", stroke: "#fff" },
  { bg: "linear-gradient(150deg,#E4EBF4,#B9C7D8)", stroke: "#0F1F38" },
];

export default function BillerList() {
  const navigate = useNavigate();
  const [isOperatorData, setOperatorData] = useState([]);
  const [isActiveOperatorData, setActiveOperatorData] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [operatorLoading, setOperatorLoading] = useState(false);
  const [search, setSearch] = useState("");
  const latestTypeRef = useRef(null);

  useEffect(() => {
    fetchOperatorType();
  }, []);

  const fetchOperatorType = async () => {
    try {
      const res = await getOperatorType();

      if (res?.statuscode === 1) {
        const types = res?.data?.assignedOpTypes || [];
        setOperatorData(types);

        if (types.length) handleSelectType(types[0]);
      }
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch operator types",
        "error",
      );
    }
  };

  const fetchActiveOperator = async (serviceID) => {
    setOperatorLoading(true);
    try {
      const payload = {
        OType: serviceID,
      };
      const res = await getActiveOperator(payload);

      if (latestTypeRef.current !== serviceID) return;

      if (res?.statuscode === 1) {
        setActiveOperatorData(res?.operators || []);
      }
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch operators",
        "error",
      );
    } finally {
      if (latestTypeRef.current === serviceID) setOperatorLoading(false);
    }
  };

  const handleSelectType = (type) => {
    latestTypeRef.current = type.serviceID;
    setSelectedType(type);
    setSearch("");
    setActiveOperatorData([]);
    fetchActiveOperator(type.serviceID);
  };

  const query = search.trim().toLowerCase();
  const filteredOperators = isActiveOperatorData.filter(
    (op) =>
      !query ||
      op.name?.toLowerCase().includes(query) ||
      String(op.billerID || op.opid || "")
        .toLowerCase()
        .includes(query),
  );

  const selectedIndex = isOperatorData.findIndex(
    (t) => t.serviceID === selectedType?.serviceID,
  );
  const selectedColor =
    CAT_COLORS[Math.max(selectedIndex, 0) % CAT_COLORS.length];

  return (
    <>
      <div className="screen active" id="screen-billers">
        <div className="page-head">
          <div>
            <h1>Biller list</h1>
            <div className="ph-sub" id="biller-sub">
              {isOperatorData.length} categories
            </div>
          </div>
        </div>

        <div className="cat-strip" id="biller-cat-strip">
          {isOperatorData.map((type, i) => {
            const color = CAT_COLORS[i % CAT_COLORS.length];
            return (
              <button
                key={type.serviceID}
                className={`cat-card ${
                  selectedType?.serviceID === type.serviceID ? "active" : ""
                }`}
                onClick={() => handleSelectType(type)}
              >
                <div className="cat-ico" style={{ background: color.bg }}>
                  <CategoryIcon name={type.name} stroke={color.stroke} />
                </div>
                <div>
                  {/* .cat-card b is styled mono for numbers — use the display font for names */}
                  <b
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: 13.5,
                      fontWeight: 700,
                      lineHeight: 1.25,
                    }}
                  >
                    {type.name}
                  </b>
                  <span>{type.service}</span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="table-toolbar">
          <div className="search-box" style={{ flex: 1, maxWidth: 420 }}>
            <svg viewBox="0 0 24 24" fill="none">
              <circle
                cx={11}
                cy={11}
                r={7}
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path
                d="M21 21l-4-4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
            <input
              id="biller-search"
              placeholder="Search by biller name or Biller ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div
            style={{ fontSize: "11.5px", color: "var(--text-faint)" }}
            id="biller-count-note"
          >
            Showing {filteredOperators.length} of {isActiveOperatorData.length}
          </div>
        </div>

        {selectedType && (
          <div id="biller-groups">
            <div className="card biller-group">
              <div className="bg-head">
                <div
                  className="cat-ico"
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 9,
                    background: selectedColor.bg,
                  }}
                >
                  <CategoryIcon
                    name={selectedType.name}
                    size={15}
                    stroke={selectedColor.stroke}
                  />
                </div>
                <h3>{selectedType.name}</h3>
                <span className="bg-count">
                  {isActiveOperatorData.length} billers
                </span>
              </div>
              <div className="table-wrap">
                <table className="data-table">
                  <tbody>
                    <tr>
                      <th>Biller name</th>
                      <th>Biller ID</th>
                      <th>BBPS</th>
                      <th className="tright">Action</th>
                    </tr>
                    {operatorLoading ? (
                      <tr>
                        <td colSpan={4} className="empty-note">
                          Loading billers...
                        </td>
                      </tr>
                    ) : filteredOperators.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="empty-note">
                          No billers found
                        </td>
                      </tr>
                    ) : (
                      filteredOperators.map((op) => (
                        <tr key={op.oid}>
                          <td className="biller-name">{op.name}</td>
                          <td className="mono">{op.billerID}</td>
                          <td>
                            {op.isBBPS ? (
                              <span className="pill pill-success">BBPS</span>
                            ) : (
                              <span className="pill pill-pending">
                                Non-BBPS
                              </span>
                            )}
                          </td>
                          <td className="tright">
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() =>
                                navigate("/add-ca-single", {
                                  state: { operator: op },
                                })
                              }
                            >
                              Add account
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
